/**
 * Shared free-text patient search for the Daily Cockpit and Recent Checks
 * search boxes — one matcher so both surfaces accept the same queries.
 *
 * Matching semantics:
 * - The query splits on whitespace/commas into tokens; a row matches when
 *   every token matches at least one field (tokens AND, fields OR). "john
 *   smith", "smith john", "smith, jo" and "smith aetna" all work — the
 *   pre-tokenized filters matched the query as one substring of a joined
 *   name ("Last, First" on the cockpit, "Last First" on Recent Checks), so
 *   any other word order silently returned nothing.
 * - Case- and diacritic-insensitive ("munoz" finds "Muñoz").
 * - Member IDs compare separator-blind on both sides: cards print
 *   "0223-61-43" while stored values mix card-formatted and bare forms. A
 *   token with no alphanumerics never matches an ID — a punctuation-only
 *   query must not match every row via `includes('')`.
 * - DOB matches the stored ISO form ("1958-03-15"), the rendered form
 *   ("03/15/1958"), complete dates in common typed formats ("3/15/1958",
 *   "03-15-1958", "19580315", "03151958"), and digit runs of 4+ ("0315",
 *   "1958"). Tokens without a digit never match DOB — a stray "-" must not
 *   match every ISO date.
 */
import { normalizeMemberId } from '../../shared/memberId'

export interface PatientSearchFields {
  firstName?: string | null
  lastName?: string | null
  /** Stored DOB — ISO "YYYY-MM-DD" everywhere today; other complete-date forms tolerated. */
  dob?: string | null
  /** Member/policy ID as stored (card-formatted or bare). */
  memberId?: string | null
  /** Any other searchable text: payer, provider, clinic, check date. */
  text?: ReadonlyArray<string | null | undefined>
}

export type PatientSearchMatcher = (fields: PatientSearchFields) => boolean

/** Lowercase + strip diacritics (NFD combining marks) so "munoz" matches "Muñoz". */
function fold(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

/**
 * Parse a completely-typed date to ISO candidates. Handles M/D/YYYY and
 * YYYY-M-D (with "/", "-" or "." separators) plus 8-digit MMDDYYYY /
 * YYYYMMDD. The 1900–2099 year guard makes the 8-digit orderings mutually
 * exclusive: a string starting with 19/20 can't be a valid MMDDYYYY month,
 * and a valid leading month can't be an in-range year.
 */
function parseDateToken(tok: string): string[] {
  const out: string[] = []
  const push = (y: number, mo: number, d: number) => {
    if (y < 1900 || y > 2099 || mo < 1 || mo > 12 || d < 1 || d > 31) return
    out.push(`${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`)
  }
  const mdy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(tok)
  if (mdy) push(Number(mdy[3]), Number(mdy[1]), Number(mdy[2]))
  const ymd = /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/.exec(tok)
  if (ymd) push(Number(ymd[1]), Number(ymd[2]), Number(ymd[3]))
  if (/^\d{8}$/.test(tok)) {
    push(Number(tok.slice(4, 8)), Number(tok.slice(0, 2)), Number(tok.slice(2, 4))) // MMDDYYYY
    push(Number(tok.slice(0, 4)), Number(tok.slice(4, 6)), Number(tok.slice(6, 8))) // YYYYMMDD
  }
  return out
}

interface DobForms {
  /** Folded string renderings (ISO, MM/DD/YYYY, raw) — substring-matched ("03/15", "1958-03"). */
  texts: string[]
  /** Canonical ISO date when the stored value parses unambiguously. */
  iso: string | null
  /** Digit-only renderings (YYYYMMDD + MMDDYYYY) for separator-less partials. */
  digits: string[]
}

function buildDobForms(raw: string | null | undefined): DobForms | null {
  const trimmed = raw?.trim()
  if (!trimmed) return null
  let iso: string | null = /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null
  if (!iso) {
    const parsed = parseDateToken(trimmed)
    if (parsed.length === 1) iso = parsed[0]
  }
  // Unparseable stored value: still findable by typing it verbatim.
  if (!iso) return { texts: [fold(trimmed)], iso: null, digits: [] }
  const [y, mo, d] = iso.split('-')
  return {
    texts: [iso, `${mo}/${d}/${y}`, fold(trimmed)],
    iso,
    digits: [`${y}${mo}${d}`, `${mo}${d}${y}`],
  }
}

function dobTokenMatches(forms: DobForms, tok: string, tokDigits: string): boolean {
  // Digit-less tokens never match DOB: "-" must not match every ISO date.
  if (tokDigits === '') return false
  if (forms.texts.some(t => t.includes(tok))) return true
  if (forms.iso !== null && parseDateToken(tok).includes(forms.iso)) return true
  // 4+ digit runs ("0315", "1958") — shorter ones are too noisy to be useful.
  return tokDigits.length >= 4 && forms.digits.some(ds => ds.includes(tokDigits))
}

/**
 * Build a per-row matcher for a search-box query, or null when the query is
 * empty (callers skip filtering entirely). Build once per query, apply per
 * row.
 */
export function buildPatientSearchMatcher(rawQuery: string): PatientSearchMatcher | null {
  const folded = fold(rawQuery.trim())
  if (!folded) return null
  const tokens = folded.split(/[\s,]+/).filter(Boolean)
  if (tokens.length === 0) return null
  const tokenIds = tokens.map(normalizeMemberId)
  const tokenDigits = tokens.map(t => t.replace(/\D/g, ''))
  // Whole-query ID form: keeps "0223 61 43" matching a stored "02236143"
  // contiguously, exactly as the pre-tokenized filters did.
  const wholeId = normalizeMemberId(folded)

  return fields => {
    const name = fold(`${fields.firstName ?? ''} ${fields.lastName ?? ''}`)
    const member = fields.memberId ? normalizeMemberId(fold(fields.memberId)) : ''
    const dob = buildDobForms(fields.dob)
    const texts = (fields.text ?? []).filter((t): t is string => !!t).map(fold)

    if (member !== '' && wholeId !== '' && member.includes(wholeId)) return true

    return tokens.every((tok, i) => {
      if (name.includes(tok)) return true
      if (texts.some(t => t.includes(tok))) return true
      if (member !== '' && tokenIds[i] !== '' && member.includes(tokenIds[i])) return true
      return dob !== null && dobTokenMatches(dob, tok, tokenDigits[i])
    })
  }
}
